import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import { Lightbulb, Newspaper, Pencil, Plus } from "lucide-react";
import { useGetList, useNotify, useTranslate, useUpdate } from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePrefersReducedMotion } from "@/hooks/use-reduced-motion";
import { cn } from "@/lib/utils";

import {
  Chip,
  EmptyState,
  LoadingCards,
  PillGroup,
  PriorityBadge,
} from "./components";
import { IdeaForm } from "./IdeaForm";
import { groupIdeasByStatus, ideaFieldPatch, type IdeaField } from "./ideas";
import {
  IDEA_STATUSES,
  ideaStatusLabelKey,
  isIdeaStatus,
  PRIORITIES,
  priorityLabelKey,
} from "./labels";
import { MiniMarkdown } from "./MiniMarkdown";
import { QuickMenu } from "./QuickMenu";
import { riseStyle } from "./util";
import { dailyItemPath } from "./routes";
import type { IdeaStatus, MarketIdea } from "./types";

/** Optimistic quick change of an idea's status or priority. */
const useIdeaQuickChange = () => {
  const [update] = useUpdate<MarketIdea>();
  const notify = useNotify();
  return <F extends IdeaField>(
    idea: MarketIdea,
    field: F,
    value: MarketIdea[F],
  ) => {
    const patch = ideaFieldPatch(idea, field, value);
    if (!patch) return;
    update("market_ideas", patch, {
      mutationMode: "optimistic",
      onSuccess: () => notify("market.ideas.updated", { type: "success" }),
      onError: () => notify("market.ideas.error", { type: "error" }),
    });
  };
};

export const IdeasView = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState<MarketIdea | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [params] = useSearchParams();
  const highlightId = params.get("idea");
  const reducedMotion = usePrefersReducedMotion();
  const { data, isPending, error } = useGetList<MarketIdea>("market_ideas", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "updated_at", order: "DESC" },
  });
  const columns = useMemo(() => groupIdeasByStatus(data ?? []), [data]);

  // Opened from a daily item ("Guardada como idea"): bring its idea into view.
  useEffect(() => {
    if (!highlightId || !data?.length) return;
    document.getElementById(ideaCardId(highlightId))?.scrollIntoView({
      block: "center",
      behavior: reducedMotion ? "auto" : "smooth",
    });
  }, [highlightId, data, reducedMotion]);

  const openForm = (idea: MarketIdea | null) => {
    setEditing(idea);
    setFormOpen(true);
  };

  return (
    <section className="space-y-4">
      <h2 className="sr-only">{translate("market.ideas.heading")}</h2>
      {isPending ? (
        <LoadingCards />
      ) : error ? (
        <EmptyState icon={Lightbulb} title={translate("market.error")} />
      ) : isMobile ? (
        <IdeasMobile
          columns={columns}
          highlightId={highlightId}
          onEdit={openForm}
          onCreate={() => openForm(null)}
        />
      ) : (
        <IdeasBoard
          columns={columns}
          highlightId={highlightId}
          onEdit={openForm}
          onCreate={() => openForm(null)}
        />
      )}
      <IdeaForm open={formOpen} onOpenChange={setFormOpen} idea={editing} />
    </section>
  );
};

type Columns = ReturnType<typeof groupIdeasByStatus>;

const ideaCardId = (id: string | number) => `mi-idea-card-${id}`;

interface IdeasLayoutProps {
  columns: Columns;
  highlightId: string | null;
  onEdit: (idea: MarketIdea) => void;
  onCreate: () => void;
}

const NewIdeaButton = ({
  onCreate,
  compact,
}: {
  onCreate: () => void;
  /** Round icon button at the end of the phone's pill row. */
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const label = translate("market.ideas.new");
  return (
    <Button
      type="button"
      onClick={onCreate}
      aria-label={compact ? label : undefined}
      title={compact ? label : undefined}
      className={cn(
        "btn-save rounded-full",
        compact ? "size-9 shrink-0 p-0" : "px-4",
      )}
    >
      <Plus aria-hidden className={compact ? "size-5" : "size-4"} />
      {compact ? null : label}
    </Button>
  );
};

const IdeasBoard = ({
  columns,
  highlightId,
  onEdit,
  onCreate,
}: IdeasLayoutProps) => {
  const translate = useTranslate();
  const change = useIdeaQuickChange();
  const all = useMemo(() => Object.values(columns).flat(), [columns]);

  const onDragEnd = ({ draggableId, destination }: DropResult) => {
    const status = destination?.droppableId;
    const idea = all.find((entry) => String(entry.id) === draggableId);
    if (idea && isIdeaStatus(status)) change(idea, "status", status);
  };

  return (
    <>
      <div className="flex justify-end">
        <NewIdeaButton onCreate={onCreate} />
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-4 gap-3">
          {IDEA_STATUSES.map((status) => (
            <Droppable key={status} droppableId={status}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  data-over={snapshot.isDraggingOver || undefined}
                  className="mi-column flex min-h-64 flex-col gap-2.5 p-2.5"
                  aria-label={translate(ideaStatusLabelKey(status))}
                >
                  <div className="flex items-center justify-between px-1.5 pt-1">
                    <h3 className="text-sm font-semibold">
                      {translate(ideaStatusLabelKey(status))}
                    </h3>
                    <span className="rounded-full bg-background px-2 text-xs font-semibold text-muted-foreground tabular-nums">
                      {columns[status].length}
                    </span>
                  </div>
                  {columns[status].map((idea, index) => (
                    <Draggable
                      key={idea.id}
                      draggableId={String(idea.id)}
                      index={index}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={cn(
                            dragSnapshot.isDragging && "rotate-[1.5deg]",
                          )}
                        >
                          <IdeaCard
                            idea={idea}
                            onEdit={onEdit}
                            highlighted={String(idea.id) === highlightId}
                            compact
                            index={index}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {columns[status].length === 0 ? (
                    <p className="px-1.5 py-6 text-center text-sm text-muted-foreground">
                      {translate("market.ideas.empty_column")}
                    </p>
                  ) : null}
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>
    </>
  );
};

const IdeasMobile = ({
  columns,
  highlightId,
  onEdit,
  onCreate,
}: IdeasLayoutProps) => {
  const translate = useTranslate();
  const [params, setParams] = useSearchParams();
  const param = params.get("estado");
  const status: IdeaStatus = isIdeaStatus(param) ? param : "nueva";
  const total = Object.values(columns).reduce(
    (sum, list) => sum + list.length,
    0,
  );
  const list = columns[status];

  return (
    <>
      <div className="mi-pills-row">
        <PillGroup<IdeaStatus>
          label={translate("market.idea_status.label")}
          value={status}
          onChange={(next) => setParams({ estado: next }, { replace: true })}
          options={IDEA_STATUSES.map((value) => ({
            value,
            label: `${translate(ideaStatusLabelKey(value))} · ${columns[value].length}`,
          }))}
        />
        <NewIdeaButton onCreate={onCreate} compact />
      </div>
      {total === 0 ? (
        <EmptyState
          icon={Lightbulb}
          title={translate("market.ideas.empty_all")}
          hint={translate("market.ideas.empty_hint")}
        />
      ) : list.length === 0 ? (
        <EmptyState
          icon={Lightbulb}
          title={translate("market.ideas.empty", {
            status: translate(ideaStatusLabelKey(status)),
          })}
        />
      ) : (
        <div className="space-y-3">
          {list.map((idea, index) => (
            <IdeaCard
              key={idea.id}
              idea={idea}
              onEdit={onEdit}
              highlighted={String(idea.id) === highlightId}
              index={index}
            />
          ))}
        </div>
      )}
    </>
  );
};

const IdeaCard = ({
  idea,
  onEdit,
  compact,
  highlighted,
  index,
}: {
  idea: MarketIdea;
  onEdit: (idea: MarketIdea) => void;
  compact?: boolean;
  highlighted?: boolean;
  index: number;
}) => {
  const translate = useTranslate();
  const change = useIdeaQuickChange();
  const titleId = `mi-idea-${idea.id}`;

  return (
    <article
      id={ideaCardId(idea.id)}
      aria-labelledby={titleId}
      aria-current={highlighted || undefined}
      data-highlight={highlighted || undefined}
      data-interactive
      className={cn(
        "mi-card mi-rise flex flex-col gap-2.5",
        compact ? "p-3.5" : "p-4",
      )}
      style={riseStyle(index)}
    >
      <div className="flex items-start gap-2">
        <h4
          id={titleId}
          className={cn(
            "min-w-0 flex-1 leading-snug font-semibold wrap-anywhere",
            compact ? "text-[15px]" : "text-[17px]",
          )}
        >
          {idea.title}
        </h4>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="-mt-1 -mr-1 shrink-0 rounded-full text-muted-foreground"
          onClick={() => onEdit(idea)}
          aria-label={`${translate("market.ideas.edit")}: ${idea.title}`}
        >
          <Pencil className="size-4" />
        </Button>
      </div>
      {idea.description ? (
        <MiniMarkdown
          source={idea.description}
          className={cn(
            "text-muted-foreground",
            compact ? "line-clamp-4 text-sm" : "text-[15px]",
          )}
        />
      ) : null}
      {idea.sector || idea.product_type ? (
        <div className="mi-chip-row">
          {idea.sector ? <Chip>{idea.sector}</Chip> : null}
          {idea.product_type ? <Chip>{idea.product_type}</Chip> : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
        <QuickMenu
          label={translate("market.ideas.change_priority")}
          value={idea.priority}
          options={PRIORITIES.map((value) => ({
            value,
            label: translate(priorityLabelKey(value)),
          }))}
          onChange={(value) => change(idea, "priority", value)}
          trigger={<PriorityBadge priority={idea.priority} />}
        />
        <QuickMenu
          label={translate("market.ideas.change_status")}
          value={idea.status}
          options={IDEA_STATUSES.map((value) => ({
            value,
            label: translate(ideaStatusLabelKey(value)),
          }))}
          onChange={(value) => change(idea, "status", value)}
          trigger={
            <span className="inline-flex h-6 items-center rounded-full bg-muted px-2.5 text-xs font-semibold">
              {translate(ideaStatusLabelKey(idea.status))}
            </span>
          }
        />
        {idea.source_item_id != null ? (
          <Link
            to={dailyItemPath(idea.source_item_id)}
            className="ml-auto inline-flex h-7 items-center gap-1 rounded-full px-1.5 text-xs font-medium text-primary hover:underline"
          >
            <Newspaper aria-hidden className="size-3.5" />
            {translate("market.ideas.source")}
          </Link>
        ) : null}
      </div>
    </article>
  );
};
